import { getSessionMedia } from '../apis/demo-media/api';

// Separate from the MediaPipe service worker's own image cache
// (public/mediapipe-sw.js) — that one is permanent/cache-first-forever and
// scoped by request destination, not by a known set of session-specific URLs.
// This one is deliberately time-bounded: a demo session's media only needs to
// stay warm long enough to bridge a weak connection during that one session,
// not forever.
const CACHE_NAME = 'session-media-prefetch-v1';
const TTL_MS = 30 * 60 * 1000;
// Carries the prefetch timestamp on the stored Response itself (rather than a
// separate index) so the cache entry is self-describing — nothing else to
// keep in sync.
const PREFETCHED_AT_HEADER = 'x-prefetched-at';

function isCacheStorageSupported() {
  return typeof caches !== 'undefined';
}

function stampWithTimestamp(response) {
  const headers = new Headers(response.headers);
  headers.set(PREFETCHED_AT_HEADER, String(Date.now()));
  return response
    .blob()
    .then(
      blob =>
        new Response(blob, { status: response.status, statusText: response.statusText, headers })
    );
}

function isExpired(response) {
  const prefetchedAt = Number(response.headers.get(PREFETCHED_AT_HEADER));
  if (!Number.isFinite(prefetchedAt)) {
    // No stamp means this wasn't written by prefetchSessionMedia — treat as
    // expired rather than trust an entry this module didn't create.
    return true;
  }
  return Date.now() - prefetchedAt > TTL_MS;
}

/**
 * Deletes every entry past its TTL. Cheap to call opportunistically — there is
 * no background timer in a page context, so this runs at the start of every
 * prefetch (a fresh session's warm-up is also the natural moment to sweep out
 * a previous one's leftovers).
 */
export async function pruneExpiredSessionMedia() {
  if (!isCacheStorageSupported()) {
    return;
  }

  try {
    const cache = await caches.open(CACHE_NAME);
    const requests = await cache.keys();

    await Promise.all(
      requests.map(async request => {
        const response = await cache.match(request);
        if (response && isExpired(response)) {
          await cache.delete(request);
        }
      })
    );
  } catch (_error) {
    // Best-effort housekeeping — a failed prune must not surface anywhere.
  }
}

/**
 * Fetches this session's exercise images/videos ahead of time and stores them
 * in Cache Storage, so a slow connection doesn't matter once the patient
 * actually reaches the page that displays them. Fire-and-forget by design:
 * never throws, never blocks the caller — a failed or incomplete prefetch
 * just means those `<img>`/`<video>` elements fall back to their normal
 * (network) URL later, exactly as if this had never run.
 */
export async function prefetchSessionMedia(sessionId, options = {}) {
  if (!isCacheStorageSupported() || !sessionId) {
    return;
  }

  await pruneExpiredSessionMedia();

  try {
    const media = await getSessionMedia(sessionId, options);
    const urls = [...new Set([...(media?.images ?? []), ...(media?.videos ?? [])].filter(Boolean))];

    if (!urls.length) {
      return;
    }

    const cache = await caches.open(CACHE_NAME);

    await Promise.all(
      urls.map(async url => {
        try {
          const existing = await cache.match(url);
          if (existing && !isExpired(existing)) {
            return;
          }

          const response = await fetch(url);
          if (!response.ok) {
            return;
          }

          await cache.put(url, await stampWithTimestamp(response));
        } catch (_error) {
          // One URL failing (offline mid-fetch, CORS, a dead link) must not
          // stop the rest of the batch.
        }
      })
    );
  } catch (_error) {
    // The session-media lookup itself failing is equally non-fatal — the
    // page this was meant to speed up still works, just without the warm cache.
  }
}

/**
 * Resolves a prefetched `blob:` URL for `url` if it's cached and still fresh,
 * or `null` otherwise (never seen, or past its TTL — which this also deletes,
 * since a lookup is a fine moment to notice it's stale). Callers fall back to
 * the original `url` on `null`.
 */
export async function getPrefetchedMediaSrc(url) {
  if (!isCacheStorageSupported() || !url) {
    return null;
  }

  try {
    const cache = await caches.open(CACHE_NAME);
    const response = await cache.match(url);

    if (!response) {
      return null;
    }

    if (isExpired(response)) {
      await cache.delete(url);
      return null;
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (_error) {
    return null;
  }
}
